import type { PaAPIm2mClient } from '@rialtic/workers-kit'
import type { PaAPI } from '@rialtic/types'
import {
  POLICY_INDICATION_EDIT_TYPE,
  formatDrugPolicyName,
  type PolicyIndicationEditType,
} from './editTypes'
import type { DdeDrugInput } from './ddeRows'
import type { PolicyReferenceDocumentIds } from './policyReferenceArtifacts'

/** Topic every auto-generated drug policy is filed under in paAPI. */
export const DRUG_POLICY_TOPIC_NAME = 'Drugs and Biological'

/** Maps each logic type to the paAPI `edit_type` name used to create its policy. */
export const DRUG_POLICY_EDIT_TYPE_NAMES: Record<
  PolicyIndicationEditType,
  string
> = {
  [POLICY_INDICATION_EDIT_TYPE.Age]: 'Age',
  [POLICY_INDICATION_EDIT_TYPE.Diagnosis]:
    'Diagnosis Valid Code Use and Medical Necessity',
  [POLICY_INDICATION_EDIT_TYPE.Frequency]: 'Units/Frequency',
  [POLICY_INDICATION_EDIT_TYPE.MaxDailyUnits]: 'Units/Frequency',
  [POLICY_INDICATION_EDIT_TYPE.MaxUnitsOverTime]: 'Units/Frequency',
}

export const DRUG_POLICY_DEFAULT_DESCRIPTION =
  'TBD - Standard drug edit description'

/** Maps each logic type to the paAPI engine id that evaluates its DDE rows. */
export const DRUG_POLICY_ENGINE_IDS: Record<PolicyIndicationEditType, string> =
  {
    [POLICY_INDICATION_EDIT_TYPE.Diagnosis]: 'drb-diagnosis',
    [POLICY_INDICATION_EDIT_TYPE.Age]: 'drb-age-cn',
    [POLICY_INDICATION_EDIT_TYPE.Frequency]: 'drb-frequency',
    [POLICY_INDICATION_EDIT_TYPE.MaxDailyUnits]: 'drb-units-dateofservice',
    [POLICY_INDICATION_EDIT_TYPE.MaxUnitsOverTime]: 'drb-units-muot',
  }

export interface DrugPolicyRefIds {
  topicId: string | undefined
  editTypeIdsByType: Partial<Record<PolicyIndicationEditType, string>>
}

/**
 * Builds the complete, replace-in-place payload for a generated drug policy.
 * History fields are intentionally omitted: those settings are configured by
 * a user later, rather than receiving a builder-side default.
 */
export const buildPolicyDetailPayload = (
  type: PolicyIndicationEditType,
  drug: DdeDrugInput,
  policyId: string,
  { topicId, editTypeId }: { topicId: string; editTypeId: string },
) => ({
  params: { path: { id: policyId } },
  body: {
    name: formatDrugPolicyName(drug, type),
    description: DRUG_POLICY_DEFAULT_DESCRIPTION,
    topic: topicId,
    edit_types: [{ edit_type_id: editTypeId }],
    // The API calls these engine identifiers `policy_engines`; its separate
    // `engine_type` field only accepts the broad `dde` or `spe` categories.
    policy_engines: [{ engine_id: DRUG_POLICY_ENGINE_IDS[type] }],
    is_claim_type_professional: true,
    is_claim_type_asc_professional: true,
    is_claim_type_asc_institutional: true,
    is_claim_type_outpatient: true,
    is_claim_type_inpatient: false,
  } satisfies PaAPI.PutBody['/policy/{id}'],
})

export interface DrugPolicyRecord {
  policyId: string
  name: string
  createdAt: string
  /** Set after the policy details have been pushed to paAPI. */
  finalizedAt?: string
  /** Primary PAAPI sources created from the finalized PDF and DDE CSV. */
  referenceDocumentIds?: Partial<PolicyReferenceDocumentIds>
  /** When an existing policy's asset was linked from Policy assets (WFL-7988). */
  assetLinks?: Partial<
    Record<
      keyof PolicyReferenceDocumentIds,
      {
        linkedAt: string
        linkedBy?: string
        /** The asset's version when it was linked, so later versions can be compared against it. */
        version?: {
          id?: string
          number: number
          effectiveStart?: string
          effectiveEnd?: string | null
        }
      }
    >
  >
  /**
   * The policy's Content Pipeline epic key (TOOL-436), e.g. `AT-123`.
   * finalize calls jira-connect synchronously (TOOL-605) so this is set in
   * the same request that creates the policy — set only when epic sync
   * succeeded; a jira sync failure surfaces in `/finalize`'s `errors` map
   * instead and leaves this (and `epicUrl`) unset.
   */
  epicId?: string
  /** Browsable Jira link for `epicId`, for "view in Jira" links in Drug
   * Builder. */
  epicUrl?: string
}

export type DrugPolicyMap = Partial<
  Record<PolicyIndicationEditType, DrugPolicyRecord>
>

export interface GenerateDrugPoliciesResult {
  policies: DrugPolicyMap
  errors: Partial<Record<PolicyIndicationEditType, string>>
}

/**
 * JSON body of `POST /finalize`, shared with the frontend so callers don't
 * treat the response as `any`. `errors` is only present on partial failure
 * (HTTP 207) — its absence (or an empty object) means every applicable type
 * completed its paAPI work. `finalized` is only present when called with
 * `createPolicies: false` (TOOL-617) — the "policies already exist" path
 * that skips policy creation/publication but still performs normal
 * finalization validation (rejects non-design drugs, concurrent requests).
 */
export interface FinalizeResponse {
  policies: DrugPolicyMap
  errors?: Partial<Record<PolicyIndicationEditType, string>>
  finalized?: boolean
}

/**
 * Resolves the "Drugs and Biological" topic id and the paAPI `edit_type` id
 * for each requested logic type by name. Names that aren't found in paAPI are
 * simply absent from the returned map so callers can surface a clear
 * per-type error instead of throwing.
 */
export async function resolveDrugPolicyRefIds(
  paapi: PaAPIm2mClient,
  types: PolicyIndicationEditType[],
): Promise<DrugPolicyRefIds> {
  const [{ data: topics }, { data: editTypes }] = await Promise.all([
    paapi.GET('/topic'),
    paapi.GET('/edit-type'),
  ])

  const topicId = topics?.find((t) => t.topic === DRUG_POLICY_TOPIC_NAME)?.id

  const editTypeIdByName = new Map(
    (editTypes ?? []).map((editType) => [editType.edit_type, editType.id]),
  )

  const editTypeIdsByType = Object.fromEntries(
    types
      .map(
        (type) =>
          [
            type,
            editTypeIdByName.get(DRUG_POLICY_EDIT_TYPE_NAMES[type]),
          ] as const,
      )
      .filter((entry): entry is [PolicyIndicationEditType, string] =>
        Boolean(entry[1]),
      ),
  ) as Partial<Record<PolicyIndicationEditType, string>>

  return { topicId, editTypeIdsByType }
}

/**
 * Creates a paAPI policy for every `applicableType` that doesn't already have
 * one in `existingPolicies` (idempotency). Each type is created independently
 * so one failure doesn't block the others — callers get back both the
 * successfully created policies and a per-type error message for the rest.
 */
export async function createMissingDrugPolicies(
  paapi: PaAPIm2mClient,
  drug: DdeDrugInput,
  applicableTypes: PolicyIndicationEditType[],
  existingPolicies: DrugPolicyMap,
  refIds?: DrugPolicyRefIds,
): Promise<GenerateDrugPoliciesResult> {
  const missingTypes = applicableTypes.filter(
    (type) => !existingPolicies[type]?.policyId,
  )

  const policies: DrugPolicyMap = {}
  const errors: GenerateDrugPoliciesResult['errors'] = {}

  if (missingTypes.length === 0) {
    return { policies, errors }
  }

  const { topicId, editTypeIdsByType } =
    refIds ?? (await resolveDrugPolicyRefIds(paapi, missingTypes))

  if (!topicId) {
    for (const type of missingTypes) {
      errors[type] = `Topic "${DRUG_POLICY_TOPIC_NAME}" not found in paAPI`
    }
    return { policies, errors }
  }

  for (const type of missingTypes) {
    const editTypeId = editTypeIdsByType[type]
    if (!editTypeId) {
      errors[type] =
        `Edit type "${DRUG_POLICY_EDIT_TYPE_NAMES[type]}" not found in paAPI`
      continue
    }

    const name = formatDrugPolicyName(drug, type)
    const { data, error } = await paapi.POST('/policy', {
      body: {
        name,
        topic: topicId,
        edit_types: [{ edit_type_id: editTypeId }],
        description: DRUG_POLICY_DEFAULT_DESCRIPTION,
        management_source: 'drug_builder',
      } satisfies PaAPI.PostBody['/policy'],
    })

    if (error || !data) {
      const typedError = error as { message?: string } | undefined
      errors[type] =
        typedError?.message ??
        JSON.stringify(typedError) ??
        'Failed to create policy'
      continue
    }

    policies[type] = {
      policyId: data.id,
      name,
      createdAt: data.created_at ?? new Date().toISOString(),
    }
  }

  return { policies, errors }
}
